import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, Package } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function PackEspecialidades() {
  const { addItem } = useCart();
  const [pricing, setPricing] = useState(null);
  const [chosen, setChosen] = useState([]);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  const packSize = pricing?.specialty_pack?.size ?? 5;
  const packPrice = pricing?.specialty_pack?.price ?? 8.0;
  const all = pricing?.all_specialties || [];
  const remaining = Math.max(0, packSize - chosen.length);
  const ready = chosen.length === packSize;

  const toggle = (name) => {
    setChosen((prev) => {
      if (prev.includes(name)) return prev.filter((s) => s !== name);
      if (prev.length >= packSize) {
        toast(`Solo puedes elegir ${packSize} especialidades en el pack`);
        return prev;
      }
      return [...prev, name];
    });
  };

  const add = () => {
    if (!ready) {
      toast(`Elige ${packSize} especialidades para completar el pack`);
      return;
    }
    const key = `pack__${[...chosen].sort().join("-")}`;
    addItem({
      key,
      kind: "specialty_pack",
      model_id: null,
      model_name: `Pack de ${packSize} especialidades`,
      size: null,
      size_label: `${packSize} separadores`,
      finish: null,
      finish_label: "",
      specialties: [...chosen],
      name_personalization: null,
      price: packPrice,
      image: "/pack-cover.svg",
      slug: "pack-especialidades",
    });
    toast.success("Pack añadido al carrito");
    setChosen([]);
  };

  if (!pricing) return null;

  return (
    <section
      id="pack-especialidades"
      data-testid="pack-section"
      className="py-24 md:py-32 bg-[#F6F1E7]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="overline mb-6">
              <span className="text-[#D7C6A3]">06</span> &nbsp;/&nbsp; Pack de especialidades
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D3732] leading-tight">
              Amplía tu carpeta con los separadores que necesites.
            </h2>
            <p className="text-[#6B705C] mt-5 font-light leading-relaxed">
              ¿Ya tienes tu VITAE o quieres personalizarla al máximo? Elige{" "}
              <strong className="font-normal text-[#2D3732]">{packSize} especialidades</strong> de
              todo el índice y llévate el pack completo por solo{" "}
              <strong className="font-normal text-[#2D3732]">{packPrice.toFixed(2)} €</strong>.
            </p>

            <div className="mt-8 bg-white border border-[#E3E0D6] rounded-2xl p-6 shadow-[0_6px_24px_-12px_rgba(45,55,50,0.15)]">
              <div className="flex items-center gap-3">
                <Package size={24} weight="light" className="text-[#8A9A6F]" />
                <div className="flex-1">
                  <p className="font-display text-2xl text-[#2D3732]">
                    {packPrice.toFixed(2)} €
                  </p>
                  <p className="text-xs text-[#6B705C]">
                    {chosen.length}/{packSize} elegidas
                    {remaining > 0 && ` · faltan ${remaining}`}
                  </p>
                </div>
              </div>

              <div className="mt-5 h-1.5 rounded-full bg-[#EBEBEB] overflow-hidden">
                <motion.div
                  className="h-full bg-[#A3B18A]"
                  initial={false}
                  animate={{ width: `${(chosen.length / packSize) * 100}%` }}
                  transition={{ duration: 0.35 }}
                />
              </div>

              <AnimatePresence>
                {chosen.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 flex flex-wrap gap-1.5"
                  >
                    {chosen.map((c) => (
                      <span
                        key={c}
                        className="text-[0.65rem] bg-[#EEF1E8] text-[#4A554E] border border-[#DDE3D2] rounded-full px-2.5 py-1"
                      >
                        {c}
                      </span>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                data-testid="pack-add-button"
                onClick={add}
                disabled={!ready}
                className="mt-5 w-full bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full py-3.5 text-sm tracking-wide transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ready ? "Añadir pack al carrito" : `Elige ${remaining} más`}
                {ready && <Plus size={16} />}
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="overline mb-4">Todo el índice</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {all.map((name) => {
                const on = chosen.includes(name);
                return (
                  <button
                    key={name}
                    data-testid={`pack-spec-${name}`}
                    onClick={() => toggle(name)}
                    className={`text-left text-sm rounded-xl border px-4 py-3 transition-all duration-200 flex items-center justify-between gap-2 ${
                      on
                        ? "bg-[#2D3732] text-white border-[#2D3732]"
                        : "bg-white text-[#4A554E] border-[#EBEBEB] hover:border-[#A3B18A]"
                    }`}
                  >
                    <span>{name}</span>
                    {on && <Check size={14} weight="bold" />}
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
